import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import {
  Add,
  GridView,
  Sort,
  FilterList,
  Folder,
  Explore,
  MoreVert,
  TrendingUp,
  Schedule,
  Pattern,
  PhotoLibrary,
  Search,
  Check,
  CalendarToday,
  SortByAlpha,
  Category,
  ViewList,
  Close,
  Rocket,
  AutoAwesome,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { useGetTopics } from '@/hooks/api/dashboard'
import { Topic } from '@/types'
import { TopicType, TopicTypes } from '@/utils/constants'
import { isBabylonType, convertToGMT } from '@/utils/helper'
import ReactTimeago from 'react-timeago'
import { ModernCreateModal } from '@/components/ModernCreateModal'
import { ModernCardSkeleton } from '@/components'
import PricingModal from '@/pages/my-profile/components/PricingModal'
import { useLocalStorage } from '@/hooks/common'

// Balanced styled components with subtle colors
const WorkspaceContainer = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #fafbff 0%, #f8fafc 100%)',
  minHeight: '100vh',
}))

const HeaderSection = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  padding: '32px',
  borderBottom: '1px solid #e2e8f0',
}))

const ContentSection = styled(Box)(() => ({
  backgroundColor: '#fafbff',
  padding: '32px',
}))

const StatsCard = styled(Card)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.08), 0 4px 16px rgba(15, 23, 42, 0.06)',
    borderColor: 'rgba(99, 102, 241, 0.15)',
  },

  '&:hover::before': {
    opacity: 1,
  },
}))

const ProjectCard = styled(Card)(() => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12), 0 4px 16px rgba(15, 23, 42, 0.08)',
    borderColor: 'rgba(99, 102, 241, 0.2)',
  },

  '&:hover::before': {
    opacity: 1,
  },
}))

const CreateButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '14px',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
  border: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
    transform: 'translateY(-1px)',
  },
}))

const QuickActionButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  color: '#1a1a1a',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  justifyContent: 'flex-start',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderColor: '#c7d2fe',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)',
    transform: 'translateY(-1px)',
  },
}))

const CleanTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '2px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    color: '#64748b',
    minHeight: '44px',
    '&.Mui-selected': {
      color: '#6366f1',
      fontWeight: 600,
    },
  },
}))

const quickActions = [
  {
    title: 'Create Pattern',
    description: 'Design new patterns',
    icon: <Pattern sx={{ fontSize: 20, color: '#7c3aed' }} />,
    action: 'pattern',
  },
  {
    title: 'Photo Album',
    description: 'Organize photos',
    icon: <PhotoLibrary sx={{ fontSize: 20, color: '#ec4899' }} />,
    action: 'album',
  },
  {
    title: 'Explore Designs',
    description: 'Discover inspiring patterns',
    icon: <Explore sx={{ fontSize: 20, color: '#10b981' }} />,
    action: 'document',
  },
]

const tabs = [
  { label: 'All', value: TopicType.ALL, icon: <Folder sx={{ fontSize: 16 }} /> },
  { label: 'Patterns', value: TopicType.PATTERN, icon: <Pattern sx={{ fontSize: 16 }} /> },
  { label: 'Albums', value: TopicType.ALBUM, icon: <PhotoLibrary sx={{ fontSize: 16 }} /> },
]

export const CleanWorkspace: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()

  // State
  const [selectedTab, setSelectedTab] = useState(TopicType.ALL)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ewUser] = useLocalStorage('ewUser', null) as [any, unknown]
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'type'>('date')
  const [searchQuery, setSearchQuery] = useState('')
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true)

  // Store
  const [topicList, userProfile] = useAppStore(
    (state) => [state.home.topicList, state.user.profile],
    shallow,
  )

  // API
  const getTopics = useGetTopics()

  useEffect(() => {
    const fetchAllTopics = async () => {
      try {
        // Fetch patterns first
        console.log('🎨 Fetching patterns...')
        const patterns = await getTopics.mutateAsync({
          pageNo: 1,
          interaction: 3, // Private workspace
          topicType: TopicType.PATTERN,
          searchType: 1,
        })
        console.log('✅ Patterns fetched:', patterns?.length || 0)

        // Get patterns from store immediately after fetch
        const patternsFromStore = { ...useAppStore.getState().home.topicList }
        console.log('💾 Patterns in store:', Object.keys(patternsFromStore).length)

        // Fetch albums with pageNo: 1 (correct for API)
        console.log('📸 Fetching albums...')
        const albums = await getTopics.mutateAsync({
          pageNo: 1,
          interaction: 3, // Private workspace
          topicType: TopicType.ALBUM,
          searchType: 1,
        })
        console.log('✅ Albums fetched:', albums?.length || 0)

        // Get albums from store
        const albumsFromStore = { ...useAppStore.getState().home.topicList }
        console.log('💾 Albums in store:', Object.keys(albumsFromStore).length)

        // Merge patterns and albums
        console.log('🔄 Merging patterns with albums...')
        const merged = { ...patternsFromStore, ...albumsFromStore }
        console.log('🔄 Merged topics:', Object.keys(merged).length)
        useAppStore.getState().setTopicList(merged)

        const finalCount = Object.keys(useAppStore.getState().home.topicList).length
        console.log('📊 Final topics in store:', finalCount)

        // Check if we might have more (if we got 20+ items, there might be more)
        setHasMore(finalCount >= 20)
      } catch (error) {
        console.error('❌ Error fetching topics:', error)
      }
    }

    fetchAllTopics()
  }, []) // Only fetch once on mount

  const loadMore = async () => {
    try {
      const nextPage = currentPage + 1
      console.log(`📄 Loading page ${nextPage}...`)

      // Fetch next page of patterns
      const patterns = await getTopics.mutateAsync({
        pageNo: nextPage,
        interaction: 3,
        topicType: TopicType.PATTERN,
        searchType: 1,
      })

      // Fetch next page of albums
      const albums = await getTopics.mutateAsync({
        pageNo: nextPage,
        interaction: 3,
        topicType: TopicType.ALBUM,
        searchType: 1,
      })

      const totalFetched = (patterns?.length || 0) + (albums?.length || 0)
      console.log(`✅ Loaded ${totalFetched} more items`)

      setCurrentPage(nextPage)
      setHasMore(totalFetched >= 20) // If we got 20+ items, there might be more
    } catch (error) {
      console.error('❌ Error loading more:', error)
    }
  }

  const topics = useMemo(() => {
    let list = Object.values(topicList)

    // Filter by search query
    if (searchQuery.trim()) {
      list = list.filter((topic) =>
        topic.topicName.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    // Filter by selected tab
    const filtered =
      selectedTab === TopicType.ALL
        ? list
        : list.filter((topic) => topic.topicType === selectedTab.toString())

    // Sort based on selected sort option
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.topicName.localeCompare(b.topicName)
        case 'type':
          return a.topicType.localeCompare(b.topicType)
        case 'date':
        default:
          return new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime()
      }
    })
  }, [topicList, selectedTab, sortBy, searchQuery])

  const stats = useMemo(() => {
    const allTopics = Object.values(topicList)

    return {
      total: allTopics.length,
      patterns: allTopics.filter((t) => t.topicType === TopicType.PATTERN.toString()).length,
      albums: allTopics.filter((t) => t.topicType === TopicType.ALBUM.toString()).length,
      recent: allTopics.filter((t) => {
        const dayAgo = new Date()
        dayAgo.setDate(dayAgo.getDate() - 7)
        return new Date(t.dateUpdated) > dayAgo
      }).length,
    }
  }, [topicList])

  const handleTabChange = (event: React.SyntheticEvent, newValue: TopicType) => {
    setSelectedTab(newValue)
  }

  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null)

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortMenuAnchor(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortMenuAnchor(null)
  }

  const handleSortSelect = (sort: 'date' | 'name' | 'type') => {
    setSortBy(sort)
    handleSortClose()
  }

  const handleProjectClick = (topic: Topic) => {
    const topicTypeStr = topic.topicType
    if (topicTypeStr === TopicType.PATTERN.toString()) {
      navigate(`/pattern?topicID=${topic.topicID}`)
    } else {
      navigate(`/preview?topicID=${topic.topicID}&topicType=${topicTypeStr}`)
    }
  }

  const handleCreateProject = (type: string, title: string) => {
    console.log('Creating project:', { type, title })
  }

  const getPreviewImage = (topic: Topic) => {
    // Use the same logic as the original Home component
    const egoList = isBabylonType(topic.topicType)
      ? topic.firstNote?.egoList || []
      : topic.latestNote?.egoList || []

    if (egoList.length > 0) {
      return egoList[0].photoAlbumURL
    }

    // Fallback to photos array if egoList is empty
    if (topic.photos && topic.photos.length > 0) {
      const firstPhoto = topic.photos[0]
      return firstPhoto.photoFullURL || firstPhoto.photoThumbURL || firstPhoto.photoAlbumURL
    }

    return undefined
  }

  const getProjectTypeIcon = (topicType: string) => {
    switch (topicType) {
      case TopicType.PATTERN.toString():
        return <Pattern sx={{ fontSize: 16, color: '#7c3aed' }} />
      case TopicType.ALBUM.toString():
        return <PhotoLibrary sx={{ fontSize: 16, color: '#ec4899' }} />
      case TopicType.GALLERY.toString():
        return <GridView sx={{ fontSize: 16, color: '#16a34a' }} />
      default:
        return <Folder sx={{ fontSize: 16, color: '#64748b' }} />
    }
  }

  const renderProjectCard = (topic: Topic) => (
    <ProjectCard
      key={topic.topicID}
      onClick={() => handleProjectClick(topic)}
      sx={{
        display: viewMode === 'list' ? 'flex' : 'block',
        flexDirection: viewMode === 'list' ? 'row' : 'column',
      }}
    >
      <CardMedia
        sx={{
          height: viewMode === 'list' ? 'auto' : 180,
          width: viewMode === 'list' ? 240 : '100%',
          minHeight: viewMode === 'list' ? 160 : 'auto',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {getPreviewImage(topic) ? (
          <>
            <img
              src={getPreviewImage(topic)}
              alt={topic.topicName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                // Hide broken image and show fallback
                e.currentTarget.style.display = 'none'
              }}
            />
            {/* Subtle overlay for better text readability */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '30%',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.1))',
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              color: '#94a3b8',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid #e2e8f0',
              }}
            >
              {getProjectTypeIcon(topic.topicType)}
            </Box>
            <Typography variant='caption' color='#64748b' fontWeight={500}>
              {TopicTypes[topic.topicType]?.title || 'Project'}
            </Typography>
          </Box>
        )}
      </CardMedia>

      <CardContent
        sx={{
          p: 2,
          flex: viewMode === 'list' ? 1 : 'none',
          display: viewMode === 'list' ? 'flex' : 'block',
          flexDirection: viewMode === 'list' ? 'column' : 'row',
        }}
      >
        {/* Type Badge - Compact pill */}
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: '8px',
              backgroundColor:
                topic.topicType === TopicType.PATTERN.toString()
                  ? 'rgba(124, 58, 237, 0.08)'
                  : topic.topicType === TopicType.ALBUM.toString()
                  ? 'rgba(236, 72, 153, 0.08)'
                  : 'rgba(34, 197, 94, 0.08)',
              border: '1px solid',
              borderColor:
                topic.topicType === TopicType.PATTERN.toString()
                  ? 'rgba(124, 58, 237, 0.15)'
                  : topic.topicType === TopicType.ALBUM.toString()
                  ? 'rgba(236, 72, 153, 0.15)'
                  : 'rgba(34, 197, 94, 0.15)',
            }}
          >
            {getProjectTypeIcon(topic.topicType)}
            <Typography
              variant='caption'
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color:
                  topic.topicType === TopicType.PATTERN.toString()
                    ? '#7c3aed'
                    : topic.topicType === TopicType.ALBUM.toString()
                    ? '#ec4899'
                    : '#16a34a',
              }}
            >
              {TopicTypes[topic.topicType]?.title || 'Project'}
            </Typography>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant='subtitle1'
          fontWeight={700}
          mb={1.5}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#0f172a',
            fontSize: '16px',
            lineHeight: '20px',
            letterSpacing: '-0.02em',
          }}
        >
          {topic.topicName}
        </Typography>

        {/* Footer - Time and Avatar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 1,
            mt: viewMode === 'list' ? 'auto' : 0,
            borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: '#94a3b8',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            <ReactTimeago date={convertToGMT(topic.dateUpdated)} />
          </Typography>

          <Avatar
            src={topic.profilePicThumbURL || userProfile?.profilePicThumbURL}
            alt={topic.userName || userProfile?.userName}
            sx={{
              width: 24,
              height: 24,
              fontSize: '11px',
              fontWeight: 600,
              bgcolor: '#e2e8f0',
              color: '#64748b',
              border: '2px solid #ffffff',
              boxShadow: '0 0 0 1px rgba(226, 232, 240, 0.8), 0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            {(topic.userName || userProfile?.userName)?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Box>
      </CardContent>
    </ProjectCard>
  )

  return (
    <WorkspaceContainer>
      {/* Header Section */}
      <HeaderSection>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}
          >
            <Box>
              <Typography variant='h4' fontWeight={700} color='#1a1a1a' mb={1}>
                Welcome back, {userProfile?.firstName || 'Creator'}
              </Typography>
              <Typography variant='body1' color='#64748b'>
                Manage your projects and create something new
              </Typography>
            </Box>

            <CreateButton
              startIcon={<Add />}
              onClick={() => setCreateModalOpen(true)}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              Create New
            </CreateButton>
          </Box>

          {/* Upgrade Banner - Shows when approaching project limit */}
          {showUpgradeBanner && stats.total >= 15 && (
            <Box
              sx={{
                position: 'relative',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                borderRadius: '20px',
                padding: '32px',
                mb: 4,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3), 0 8px 24px rgba(0, 0, 0, 0.15)',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
                  pointerEvents: 'none',
                },

                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-10%',
                  width: '400px',
                  height: '400px',
                  background:
                    'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                },
              }}
            >
              <IconButton
                onClick={() => setShowUpgradeBanner(false)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  color: 'rgba(255, 255, 255, 0.9)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  width: 36,
                  height: 36,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Close fontSize='small' />
              </IconButton>

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    gap: 3,
                  }}
                >
                  {/* Left side - Icon + Content */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Rocket sx={{ fontSize: 32, color: '#ffffff' }} />
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography
                          variant='h5'
                          sx={{
                            color: '#ffffff',
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '20px', md: '24px' },
                          }}
                        >
                          You're on fire! 🔥
                        </Typography>
                        <AutoAwesome sx={{ color: '#fbbf24', fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant='body1'
                        sx={{
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 500,
                          lineHeight: 1.6,
                          maxWidth: '500px',
                        }}
                      >
                        You have {stats.total} amazing projects! Upgrade to Pro for unlimited
                        projects, advanced templates, and priority support.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side - CTA Button */}
                  <Button
                    startIcon={<Rocket />}
                    onClick={() => {
                      setPricingModalOpen(true)
                    }}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#667eea',
                      fontWeight: 700,
                      fontSize: '15px',
                      padding: '12px 32px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                      border: 'none',
                      letterSpacing: '-0.01em',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      marginRight: '40px',

                      '&:hover': {
                        background: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                      },

                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    Upgrade to Pro
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={6} md={3}>
              <StatsCard>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <TrendingUp sx={{ fontSize: 24, mb: 1, color: '#10b981' }} />
                  <Typography variant='h5' fontWeight={700} color='#1a1a1a'>
                    {stats.total}
                  </Typography>
                  <Typography variant='body2' color='#64748b'>
                    Total Projects
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>

            <Grid item xs={6} md={3}>
              <StatsCard>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Pattern sx={{ fontSize: 24, mb: 1, color: '#7c3aed' }} />
                  <Typography variant='h5' fontWeight={700} color='#1a1a1a'>
                    {stats.patterns}
                  </Typography>
                  <Typography variant='body2' color='#64748b'>
                    Patterns
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>

            <Grid item xs={6} md={3}>
              <StatsCard>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <PhotoLibrary sx={{ fontSize: 24, mb: 1, color: '#ec4899' }} />
                  <Typography variant='h5' fontWeight={700} color='#1a1a1a'>
                    {stats.albums}
                  </Typography>
                  <Typography variant='body2' color='#64748b'>
                    Albums
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>

            <Grid item xs={6} md={3}>
              <StatsCard>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Schedule sx={{ fontSize: 24, mb: 1, color: '#f59e0b' }} />
                  <Typography variant='h5' fontWeight={700} color='#1a1a1a'>
                    {stats.recent}
                  </Typography>
                  <Typography variant='body2' color='#64748b'>
                    This Week
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Box>
            <Typography variant='h6' fontWeight={600} mb={3} color='#1a1a1a'>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <QuickActionButton
                    fullWidth
                    onClick={() => {
                      if (action.action === 'pattern') {
                        setCreateModalOpen(true)
                      } else if (action.action === 'album') {
                        navigate('/workspace/album')
                      } else if (action.action === 'document') {
                        navigate('/discover/pattern')
                      }
                    }}
                    startIcon={action.icon}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant='subtitle2' fontWeight={600} color='#1a1a1a'>
                        {action.title}
                      </Typography>
                      <Typography variant='caption' color='#64748b'>
                        {action.description}
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </HeaderSection>

      {/* Content Section */}
      <ContentSection>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Header with Search */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
            >
              <Typography variant='h5' fontWeight={700} color='#1a1a1a' letterSpacing='-0.02em'>
                Your Projects
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* View Mode Toggle */}
                <Tooltip title={viewMode === 'grid' ? 'Grid View' : 'List View'} arrow>
                  <IconButton
                    size='small'
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor:
                        viewMode === 'grid' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                      color: viewMode === 'grid' ? '#6366f1' : '#64748b',
                      border: '1px solid',
                      borderColor: viewMode === 'grid' ? 'rgba(99, 102, 241, 0.2)' : '#e2e8f0',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        borderColor: 'rgba(99, 102, 241, 0.2)',
                        color: '#6366f1',
                      },
                    }}
                  >
                    {viewMode === 'grid' ? (
                      <GridView fontSize='small' />
                    ) : (
                      <ViewList fontSize='small' />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Sort Button with Menu */}
                <Tooltip title='Sort by' arrow>
                  <IconButton
                    size='small'
                    onClick={handleSortClick}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: sortMenuAnchor ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                      color: sortMenuAnchor ? '#6366f1' : '#64748b',
                      border: '1px solid',
                      borderColor: sortMenuAnchor ? 'rgba(99, 102, 241, 0.2)' : '#e2e8f0',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        borderColor: 'rgba(99, 102, 241, 0.2)',
                        color: '#6366f1',
                      },
                    }}
                  >
                    <Sort fontSize='small' />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={sortMenuAnchor}
                  open={Boolean(sortMenuAnchor)}
                  onClose={handleSortClose}
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      mt: 1,
                      minWidth: 200,
                    },
                  }}
                >
                  <MenuItem
                    selected={sortBy === 'date'}
                    onClick={() => handleSortSelect('date')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'date' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <CalendarToday fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Date Updated</ListItemText>
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === 'name'}
                    onClick={() => handleSortSelect('name')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'name' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <SortByAlpha fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Name</ListItemText>
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === 'type'}
                    onClick={() => handleSortSelect('type')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'type' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <Category fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Type</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder='Search projects...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#c7d2fe',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px 12px 0',
                },
              }}
            />
          </Box>

          {/* Tabs */}
          <CleanTabs
            value={selectedTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            sx={{ mb: 4 }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.icon}
                    {tab.label}
                  </Box>
                }
                value={tab.value}
              />
            ))}
          </CleanTabs>

          {/* Projects Grid */}
          {getTopics.isLoading ? (
            <ModernCardSkeleton count={8} />
          ) : topics.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Folder sx={{ fontSize: 48, mb: 2, color: '#cbd5e1' }} />
              <Typography variant='h6' fontWeight={500} mb={1} color='#64748b'>
                No projects yet
              </Typography>
              <Typography variant='body2' mb={3} color='#94a3b8'>
                Create your first project to get started
              </Typography>
              <CreateButton startIcon={<Add />} onClick={() => setCreateModalOpen(true)}>
                Create Project
              </CreateButton>
            </Box>
          ) : (
            <>
              <Grid container spacing={viewMode === 'list' ? 2 : 3}>
                {topics.map((topic) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === 'list' ? 12 : 6}
                    md={viewMode === 'list' ? 12 : 4}
                    lg={viewMode === 'list' ? 12 : 3}
                    key={topic.topicID}
                  >
                    {renderProjectCard(topic)}
                  </Grid>
                ))}
              </Grid>

              {/* Load More Button */}
              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <CreateButton
                    onClick={loadMore}
                    disabled={getTopics.isLoading}
                    sx={{ minWidth: '200px' }}
                  >
                    {getTopics.isLoading ? (
                      <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                    ) : (
                      'Load More'
                    )}
                  </CreateButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </ContentSection>

      {/* Create Modal */}
      <ModernCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateProject={handleCreateProject}
      />
      <PricingModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} userId={ewUser?.userID} userEmail={ewUser?.userName} />
    </WorkspaceContainer>
  )
}

export default CleanWorkspace
